"use client";

import { getUserLocalCacheByCurrentUser } from "@/actions/userLocalCacheActions";
import { Footer } from "@/components/footer/Footer";
import { Header, HEADER_HEIGHT } from "@/components/Header";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import ShareIcon from "@mui/icons-material/Share";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  Typography,
  Chip,
  Badge,
} from "@mui/material";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { ContextLink } from "@/components/common/ContextLink";
import React, { PropsWithChildren, useEffect, useState } from "react";
import { logOut } from "@/utils/auth";
import { useSelect } from "@/context/ContextSelector";
import { FolderOpen, Groups } from "@mui/icons-material";
import { viruses } from "@prisma/client";
import { usePathname } from "next/navigation";
import useUserNotificationStore from "@/stores/userNotificationStore";
import { UserLocalCacheSafe } from "@/utils/types/userLocalCache";

export default function HeaderLayout({ children }: PropsWithChildren) {
  const { contextValue, setContextValue } = useSelect();

  const { data } = useSession() as any;
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  const { pendingNotifications, refreshPendingNotifications } =
    useUserNotificationStore();

  useEffect(() => {
    setIsLoggedIn(
      !(data === null || data?.error === "RefreshAccessTokenError"),
    );
  }, [data]);

  /**
   * Listen for logout event from other tabs or windows.
   */
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      return;
    }

    const channel = new BroadcastChannel("auth-updates");

    channel.onmessage = (event) => {
      if (event.data.type === "LOGOUT") {
        // Logout this tab when another tab logs out (looping with false param)
        logOut(false);
      }
    };

    return () => {
      channel.close();
    };
  }, []);

  /**
   * Refresh the user's pending notifications.
   */
  useEffect(() => {
    const getNotificationCount = async () => {
      let currentUser: UserLocalCacheSafe | null = null;
      try {
        currentUser = await getUserLocalCacheByCurrentUser();
      } catch {}

      if (currentUser) {
        try {
          await refreshPendingNotifications();
        } catch {
          console.error("Error fetching notification count.");
        }
      }
    };

    getNotificationCount();
  }, [refreshPendingNotifications]);

  const handleLogout = () => {
    try {
      logOut();
    } catch (e) {
      console.error(e);
    }
  };

  const [isImportSpecDropdownOpen, setIsImportSpecDropdownOpen] =
    useState(false);
  const [isContributeDropdownOpen, setIsContributeDropdownOpen] =
    useState(false);

  // Show an "In progress" chip next to Datasets when user is mapping IDs via import_for_dataset/<id>
  const pathname = usePathname();
  const isExplorePage = !!pathname && /^\/explore(\/.*|\?.*)?$/.test(pathname);

  const showDatasetsInProgressChip =
    !!pathname &&
    (/^\/import_for_dataset\/\d+/.test(pathname) ||
      /^\/sequence_for_dataset\/\d+/.test(pathname) ||
      /^\/import_for_working_group\/\d+/.test(pathname) ||
      /^\/sequence_for_working_group\/\d+/.test(pathname));

  const handleImportSpecDropdownToggle = () =>
    setIsImportSpecDropdownOpen(!isImportSpecDropdownOpen);

  const handleContributeDropdownToggle = () =>
    setIsContributeDropdownOpen(!isContributeDropdownOpen);

  return (
    <>
      <Header />
      <Box
        display="flex"
        flexDirection="column"
        height={isExplorePage ? "100vh" : undefined}
        minHeight={isExplorePage ? undefined : "100vh"}
        overflow={isExplorePage ? "hidden" : undefined}
      >
        <Box display="flex" flexGrow={1}>
          <Drawer
            variant="permanent"
            sx={{
              width: 256,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                width: 256,
                boxSizing: "border-box",
              },
            }}
          >
            <Box sx={{ display: "flex" }}>
              <ContextLink href="/">
                <Image
                  src={`/assets/logo/Arbogen${
                    contextValue ? "_" + contextValue : ""
                  }.svg`}
                  alt="Arbogen-Logo"
                  width={256}
                  height={87}
                  priority
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </ContextLink>
            </Box>
            <List>
              <ContextLink href="/contribute" className="hiddenLink">
                <ListItemButton>
                  <ListItemIcon className="menuIcon">
                    <ShareIcon />
                  </ListItemIcon>
                  <Typography variant="subtitle2" sx={{ ml: 3 }}>
                    Contribute
                  </Typography>
                  <ListItemIcon sx={{ ml: "auto" }}>
                    {isContributeDropdownOpen ? (
                      <ExpandLessIcon
                        onClick={handleContributeDropdownToggle}
                      />
                    ) : (
                      <ExpandMoreIcon
                        onClick={handleContributeDropdownToggle}
                      />
                    )}
                  </ListItemIcon>
                </ListItemButton>
              </ContextLink>
              {isContributeDropdownOpen && (
                <Box sx={{ ml: 4 }}>
                  <ContextLink
                    href="/docs/Governance Data/Licenses_And_Rights"
                    className="hiddenLink"
                    skipContext
                  >
                    <ListItemButton>
                      <ListItemIcon className="menuIcon">
                        <PolicyOutlinedIcon />
                      </ListItemIcon>
                      <Typography variant="subtitle2">
                        Data Governance
                      </Typography>
                    </ListItemButton>
                  </ContextLink>
                </Box>
              )}
              {
                <IsLoggedInListItemWrapper
                  href="/import"
                  icon={<FileDownloadOutlinedIcon />}
                  text="Import data"
                  isLoggedIn={isLoggedIn}
                />
              }
              {
                <IsLoggedInListItemWrapper
                  href="/explore/all"
                  icon={<TrendingUpIcon />}
                  text="Explore data"
                  isLoggedIn={isLoggedIn}
                />
              }
              {/* TODO: enable the Analyze when ready
              {contextValue === viruses.DENV && (
                <IsLoggedInListItemWrapper
                  href="/analyze"
                  icon={<AccountTreeOutlinedIcon />}
                  text="Analyze"
                  isLoggedIn={isLoggedIn}
                />
              )}
              */}
              {
                <IsLoggedInListItemWrapper
                  href="/datasets/mine"
                  icon={<FolderOpen />}
                  text="Datasets"
                  isLoggedIn={isLoggedIn}
                  endAdornment={
                    showDatasetsInProgressChip ? (
                      <Chip
                        size="small"
                        label="In progress"
                        sx={(t) => ({
                          bgcolor: t.palette.success.main,
                          color: t.palette.primary.contrastText,
                        })}
                      />
                    ) : undefined
                  }
                />
              }
              {
                <IsLoggedInListItemWrapper
                  href="/working_groups"
                  icon={<Groups />}
                  text="Working groups"
                  isLoggedIn={isLoggedIn}
                />
              }
              <Divider />
              <ContextLink
                href="/docs"
                target="_blank"
                className="hiddenLink"
                skipContext
              >
                <ListItemButton>
                  <ListItemIcon className="menuIcon">
                    <DescriptionOutlinedIcon />
                  </ListItemIcon>
                  <Typography variant="subtitle2" sx={{ ml: 3 }}>
                    Documentation
                  </Typography>
                </ListItemButton>
              </ContextLink>
              <Divider />
              {!isLoggedIn && (
                <ContextLink href="/login" className="hiddenLink">
                  <ListItemButton>
                    <ListItemIcon className="menuIcon">
                      <LoginIcon />
                    </ListItemIcon>
                    <Typography variant="subtitle2" sx={{ ml: 3 }}>
                      Login
                    </Typography>
                  </ListItemButton>
                </ContextLink>
              )}
              {
                <IsLoggedInListItemWrapper
                  href="/account"
                  icon={
                    <Badge
                      badgeContent={pendingNotifications.length}
                      color="info"
                    >
                      <AccountCircleOutlinedIcon />
                    </Badge>
                  }
                  text="Account"
                  isLoggedIn={isLoggedIn}
                />
              }
              {
                <IsLoggedInListItemWrapper
                  onClick={handleLogout}
                  href=""
                  icon={<LogoutIcon />}
                  text="Signout"
                  isLoggedIn={isLoggedIn}
                />
              }
            </List>
          </Drawer>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              mx: 1,
              my: 1,
              mt: `calc(${HEADER_HEIGHT} + 16px)`,
              width: "calc(100% - 400px)",
              ...(isExplorePage && {
                height: `calc(100vh - ${HEADER_HEIGHT} - 24px)`,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }),
            }}
          >
            {children}
          </Box>
        </Box>
        {!/^\/explore(\/.*|\?.*)?$/.test(pathname) &&
          !/^\/analyze(\/.*|\?.*)?$/.test(pathname) && <Footer />}
      </Box>
    </>
  );
}

const IsLoggedInListItemWrapper = ({
  href,
  onClick,
  icon,
  text,
  isLoggedIn,
  endAdornment,
}: {
  href: string;
  onClick?: () => void;
  icon: React.ReactNode;
  text: string;
  isLoggedIn: boolean;
  endAdornment?: React.ReactNode;
}) => {
  return isLoggedIn ? (
    <ContextLink href={href} onClick={onClick} className="hiddenLink">
      <ListItemButton>
        <ListItemIcon className="menuIcon">{icon}</ListItemIcon>
        <Typography variant="subtitle2" sx={{ ml: 3 }}>
          {text}
        </Typography>
        {endAdornment && (
          <ListItemIcon sx={{ ml: "auto" }}>{endAdornment}</ListItemIcon>
        )}
      </ListItemButton>
    </ContextLink>
  ) : null;
};
